import { Globe, Server } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@shared/components/ui/cn';

import type { AiDataPolicyChoice } from '../types';

import { PolicyPoint } from './PolicyPoint';

export interface PolicyOptionCardProps {
  value: AiDataPolicyChoice;
  name: string;
  checked: boolean;
  current?: boolean;
  disabled?: boolean;
  onSelect: (value: AiDataPolicyChoice) => void;
  className?: string;
}

const COPY: Record<AiDataPolicyChoice, string> = {
  investbanq_only: 'investbanqOnly',
  commercial_allowed: 'commercialAllowed',
};

export function PolicyOptionCard({
  value,
  name,
  checked,
  current = false,
  disabled = false,
  onSelect,
  className,
}: PolicyOptionCardProps) {
  const { t } = useTranslation('ai-data-policy');
  const id = useId();
  const titleId = `${id}-title`;
  const badgeId = `${id}-badge`;
  const summaryId = `${id}-summary`;
  const copy = `option.${COPY[value]}`;
  const Glyph = value === 'investbanq_only' ? Server : Globe;

  return (
    <label
      htmlFor={id}
      data-checked={checked ? '' : undefined}
      className={cn(
        'relative flex min-w-0 flex-1 flex-col gap-3 rounded-lg border bg-card p-4 text-start transition-colors',
        checked ? 'border-foreground' : 'border-border',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        'has-[:focus-visible]:ring-[3px] has-[:focus-visible]:ring-neutral-300 dark:has-[:focus-visible]:ring-neutral-600',
        className,
      )}
    >
      <span className="flex items-start gap-3">
        <span className="relative flex h-5 w-4 shrink-0 items-center">
          <input
            id={id}
            type="radio"
            name={name}
            value={value}
            checked={checked}
            disabled={disabled}
            aria-labelledby={current ? `${titleId} ${badgeId}` : titleId}
            aria-describedby={summaryId}
            onChange={() => {
              onSelect(value);
            }}
            className="peer sr-only"
          />
          <span
            aria-hidden
            className="pointer-events-none flex size-4 items-center justify-center rounded-full border border-neutral-500 bg-background peer-checked:border-foreground peer-checked:[&>span]:scale-100"
          >
            <span className="size-2 scale-0 rounded-full bg-foreground transition-transform duration-100" />
          </span>
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex flex-wrap items-center gap-x-2">
            <span id={titleId} className="text-sm font-medium text-foreground">
              {t(`${copy}.title`)}
            </span>
            {current && (
              <span
                id={badgeId}
                className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-foreground"
              >
                {t('option.current')}
              </span>
            )}
          </span>
          <span id={summaryId} className="text-sm text-muted-foreground">
            {t(`${copy}.summary`)}
          </span>
        </span>
        <Glyph className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      </span>

      <span className="flex flex-col gap-3 ps-7">
        <span className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">{t('option.benefits')}</span>
          <ul className="flex flex-col gap-1.5">
            <PolicyPoint type="benefit">{t(`${copy}.benefit1`)}</PolicyPoint>
            <PolicyPoint type="benefit">{t(`${copy}.benefit2`)}</PolicyPoint>
          </ul>
        </span>
        <span className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">{t('option.tradeoffs')}</span>
          <ul className="flex flex-col gap-1.5">
            <PolicyPoint type="tradeoff">{t(`${copy}.tradeoff1`)}</PolicyPoint>
            <PolicyPoint type="tradeoff">{t(`${copy}.tradeoff2`)}</PolicyPoint>
          </ul>
        </span>
      </span>
    </label>
  );
}
